import { z } from 'zod/v4';
import { PillarCode } from '../session/orchestration/tool-protocol/follow-up-refs.js';
import { ATTACHED_RESULT_SCHEMA_VERSION } from './attached-result-constants.js';
import { AttachedResultKind } from './enums.js';
import { flightsPayloadSchema } from './payloads/flights.contracts.js';
import { hotelsPayloadSchema } from './payloads/hotels.contracts.js';
import { linksPayloadSchema } from './payloads/links.contracts.js';

// ════════════════════════════════════════════════════════════════════════════
// The attached-result envelope schemas (U17, §7.4) — zod, the single authority.
//
// SELF-CONTAINED BY REQUIREMENT. The record store is ephemeral (U1) and the
// block is persisted forever, so the block must render with zero reads back
// into BL state — now, and after the attempt, the session and the store that
// produced it are all gone. Everything a component needs is in here or it does
// not exist.
//
// The static types are `z.infer`'d from these schemas in `envelope.ts`, so the
// runtime validator and the compile-time shape cannot drift: there is no
// hand-written interface for a schema to fall out of step with.
// ════════════════════════════════════════════════════════════════════════════

/**
 * The first of U17's three exhaustive registries.
 *
 * `satisfies Record<AttachedResultKind, z.ZodType>` is the hermetic rule's
 * compiler half: adding a member to `AttachedResultKind` without adding its
 * schema here fails to compile. The object is NOT annotated as that Record —
 * `satisfies` keeps each entry's own schema type, which is what lets
 * `AttachedResultPayloadByKind` infer a real payload type per kind instead of
 * `unknown`.
 */
export const attachedResultPayloadSchemaByKind = {
  [AttachedResultKind.Links]: linksPayloadSchema,
  [AttachedResultKind.Hotels]: hotelsPayloadSchema,
  [AttachedResultKind.Flights]: flightsPayloadSchema,
} as const satisfies Record<AttachedResultKind, z.ZodType>;

/** Where the data came from. `provider` is the pillar-scoped two-letter lane
 *  code, null on a multi-lane aggregate search — the same convention
 *  `SearchRefRecord.provider` already carries. */
export const attachedResultSourceSchema = z.object({
  pillar: z.enum(PillarCode),
  provider: z.string().nullable(),
});

/** The fields between the discriminator and the payload. Kept apart from
 *  `schema` and `kind` so that a rendered envelope's KEY ORDER is §7.4's own —
 *  the JSON is read by people as well as parsers, and the two identifying
 *  fields belong at the top of it. */
const attachedResultEnvelopeMiddle = {
  /** Per-kind payload version (>= 1). Bumped when a kind's payload changes
   *  shape; an FE meeting a newer one renders the neutral fallback card. */
  v: z.number().int().min(1),
  /** ISO-8601 — the SEARCH's capture time (U16), read from the record. Not
   *  the time the block was pasted: the caption states when the data was true. */
  capturedAt: z.string(),
  source: attachedResultSourceSchema,
  /** Authored in the attach block's `title:`. Null when none was written. */
  title: z.string().nullable(),
};

function envelopeSchemaFor<K extends AttachedResultKind>(kind: K) {
  return z.object({
    schema: z.literal(ATTACHED_RESULT_SCHEMA_VERSION),
    kind: z.literal(kind),
    ...attachedResultEnvelopeMiddle,
    payload: attachedResultPayloadSchemaByKind[kind],
  });
}

/**
 * The persisted envelope, as a discriminated union over `kind`.
 *
 * The member list is written out rather than derived from the registry
 * because `z.discriminatedUnion` needs its options statically. Totality is not
 * left to review: `AssertEveryKindIsInTheEnvelopeUnion` (envelope.ts) fails to
 * compile the moment a kind is missing from it.
 */
export const attachedResultEnvelopeSchema = z.discriminatedUnion('kind', [
  envelopeSchemaFor(AttachedResultKind.Links),
  envelopeSchemaFor(AttachedResultKind.Hotels),
  envelopeSchemaFor(AttachedResultKind.Flights),
]);
